/**
 * @file api-notifications.ts
 * @description API for managing in-app notifications for admins
 * @author XiaVan Team
 * 
 * Features:
 * - Create notification when access request is created
 * - Get unread notifications count
 * - Get all notifications
 * - Mark notification as read
 * - Delete notification (when request is processed)
 * - Real-time listener for notifications
 */

import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from './firebase'
import type { Notification, NotificationType } from '@/types/user'

/**
 * Create a notification
 * Called when a new access request is created
 * 
 * @param type - Notification type
 * @param title - Notification title
 * @param message - Notification message
 * @param relatedRequestId - Optional: ID of related access request
 * @param relatedEmail - Optional: Email of user who triggered the notification
 * @returns Created notification ID
 */
export async function createNotification(
  type: NotificationType,
  title: string,
  message: string,
  relatedRequestId?: string,
  relatedEmail?: string
): Promise<string> {
  try {
    const notificationsRef = collection(db, 'notifications')
    const notification: Omit<Notification, 'id'> = {
      type,
      title,
      message,
      read: false,
      createdAt: Timestamp.now(),
      relatedRequestId,
      relatedEmail,
    }

    const docRef = await addDoc(notificationsRef, notification)
    console.log('🔔 Notification created:', docRef.id)
    return docRef.id
  } catch (error) {
    console.error('Error creating notification:', error)
    throw error
  }
}

/**
 * Get unread notifications count
 * Used for badge counter
 * 
 * @returns Number of unread notifications
 */
export async function getUnreadNotificationsCount(): Promise<number> {
  try {
    const notificationsRef = collection(db, 'notifications')
    const q = query(notificationsRef, where('read', '==', false))
    const snapshot = await getDocs(q)
    return snapshot.size
  } catch (error) {
    console.error('Error getting unread notifications count:', error)
    return 0
  }
}

/**
 * Get all notifications
 * Ordered by creation date (newest first)
 * 
 * @param onlyUnread - Optional: Only return unread notifications
 * @returns Array of notifications
 */
export async function getNotifications(onlyUnread = false): Promise<Notification[]> {
  try {
    const notificationsRef = collection(db, 'notifications')
    let q = query(notificationsRef, orderBy('createdAt', 'desc'))

    if (onlyUnread) {
      q = query(notificationsRef, where('read', '==', false), orderBy('createdAt', 'desc'))
    }

    const snapshot = await getDocs(q)
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Notification[]
  } catch (error) {
    console.error('Error getting notifications:', error)
    return []
  }
}

/**
 * Mark notification as read
 * 
 * @param notificationId - ID of notification to mark as read
 */
export async function markNotificationAsRead(notificationId: string): Promise<void> {
  try {
    const notificationRef = doc(db, 'notifications', notificationId)
    await updateDoc(notificationRef, {
      read: true,
    })
    console.log('✅ Notification marked as read:', notificationId)
  } catch (error) {
    console.error('Error marking notification as read:', error)
    throw error
  }
}

/**
 * Delete notification
 * Called when related access request is processed (approved/denied)
 * 
 * @param notificationId - ID of notification to delete
 */
export async function deleteNotification(notificationId: string): Promise<void> {
  try {
    const notificationRef = doc(db, 'notifications', notificationId)
    await deleteDoc(notificationRef)
    console.log('🗑️ Notification deleted:', notificationId)
  } catch (error) {
    console.error('Error deleting notification:', error)
    throw error
  }
}

/**
 * Delete notification by related request ID
 * Useful when processing access requests
 * 
 * @param requestId - ID of related access request
 */
export async function deleteNotificationByRequestId(requestId: string): Promise<void> {
  try {
    const notificationsRef = collection(db, 'notifications')
    const q = query(notificationsRef, where('relatedRequestId', '==', requestId))
    const snapshot = await getDocs(q)

    const deletePromises = snapshot.docs.map((doc) => deleteDoc(doc.ref))
    await Promise.all(deletePromises)

    console.log(`🗑️ Deleted ${snapshot.size} notifications for request ${requestId}`)
  } catch (error) {
    console.error('Error deleting notifications by request ID:', error)
    throw error
  }
}

/**
 * Real-time listener for notifications
 * Listens for changes in notifications collection
 * 
 * @param callback - Function to call when notifications change
 * @returns Unsubscribe function
 */
export function listenToNotifications(
  callback: (notifications: Notification[]) => void
): Unsubscribe {
  const notificationsRef = collection(db, 'notifications')
  const q = query(notificationsRef, orderBy('createdAt', 'desc'))

  return onSnapshot(q, (snapshot) => {
    const notifications = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Notification[]
    callback(notifications)
  })
}

/**
 * Real-time listener for unread notifications count
 * 
 * @param callback - Function to call when unread count changes
 * @returns Unsubscribe function
 */
export function listenToUnreadCount(callback: (count: number) => void): Unsubscribe {
  const notificationsRef = collection(db, 'notifications')
  const q = query(notificationsRef, where('read', '==', false))

  return onSnapshot(q, (snapshot) => {
    callback(snapshot.size)
  })
}
